import { useMemo, useCallback } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MatchHub } from '../../../../../components/match/MatchHub';
import { useMatchHubData } from '../../../../../src/match/hooks/useMatchHubData';
import { useTheme } from '../../../../../src/theme/useTheme';
import { getMatchHubStyles } from '../../../../../components/match/MatchHub/MatchHub.styles';
import { competitionBasePath } from '../../../../../src/competitions/helpers/competitionRoutes.helper';

/**
 * The generic twin of app/app/worldcup/match/[gameId].tsx (specs/champions-league AC-10.3).
 * The match hub is already competition-agnostic — it keys off the match alone — so this
 * route is the World Cup's, unchanged.
 */
export default function CompetitionMatchHubRoute() {
  const router = useRouter();
  const theme = useTheme();
  const layoutStyles = useMemo(() => getMatchHubStyles(theme), [theme]);
  const { gameId, competitionId: rawCompetitionId } =
    useLocalSearchParams<{ gameId: string; competitionId: string }>();
  const id = typeof gameId === 'string' ? gameId : '';
  const competitionId = rawCompetitionId ?? '';
  const { data, isLoading } = useMatchHubData(id);

  // A refresh or a deep link opens this route with no history, and an unconditional
  // back() would leave the button inert. Fall back to this competition's picks tab, which
  // is the tree the card was tapped in — never the World Cup's.
  const handleBack = useCallback(() => {
    if (router.canGoBack()) router.back();
    else router.replace(competitionBasePath(competitionId) as never);
  }, [router, competitionId]);

  return (
    <SafeAreaView style={[layoutStyles.flex, layoutStyles.bodyBg]} edges={['top', 'left', 'right']}>
      {isLoading || data === null ? (
        <View style={layoutStyles.flex}>
          <ActivityIndicator style={layoutStyles.flex} color={theme.primary} />
        </View>
      ) : (
        <MatchHub data={data} onBack={handleBack} />
      )}
    </SafeAreaView>
  );
}
